'use client';

import { useLogger } from 'next-axiom';
import { useTransition } from 'react';
import { toast } from 'sonner';
import { useElevarEvents } from '../../elevar';
import { serializeCartItemToElevarProduct } from '../../elevar/elevar-helpers';
import { usePreviousPage } from '../../lib/hooks/use-previous-page';
import { editQuantityAction } from '../actions';
import { useCart } from '../context';
import type { ContextCartItem } from '../types';
import { useClearCartCache } from './use-clear-cart-cache';

export function useEditQuantity({ domain }: { domain: string }) {
  const { clearCartCache } = useClearCartCache();
  const { updateCartItem, loading, setLoading } = useCart();
  const [_, startTransition] = useTransition();
  const previousPage = usePreviousPage();
  const { trackAddToCart, trackRemoveFromCart } = useElevarEvents();
  const log = useLogger().with({ location: 'useEditQuantity' });

  const editQuantity = (
    item: ContextCartItem,
    type: 'plus' | 'minus',
    maxQuantityAvailable: number
  ) => {
    setLoading(true);

    const newQuantity = type === 'plus' ? item.quantity + 1 : item.quantity - 1;

    // For plus operations, validate against inventory limits
    if (
      type === 'plus' &&
      !item.allowBackorder &&
      newQuantity > maxQuantityAvailable
    ) {
      const availableToAdd = Math.max(0, maxQuantityAvailable - item.quantity);
      const isTile = item.category === 'TILE';

      let error: string;
      if (isTile) {
        // For tiles, we'd need squareMetersPerBox to show area, but we don't have it in cart items
        error = 'Cannot add more items - insufficient inventory available.';
      } else {
        error = `Only ${availableToAdd} item${availableToAdd === 1 ? '' : 's'} available to add to cart.`;
      }

      setLoading(false);
      toast.error(error);
      return;
    }

    const success = updateCartItem({
      merchandiseId: item.variantId,
      updateType: type,
    });

    // Only proceed if validation passed
    if (!success) {
      setLoading(false);
      return;
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    startTransition(async () => {
      try {
        const actionResponse = await editQuantityAction({
          merchandiseId: item.variantId,
          quantity: newQuantity,
        });

        if (!actionResponse) {
          log.error('editQuantityAction returned undefined');
          clearCartCache();
          return;
        }

        if (actionResponse.serverError) {
          log.error('serverError', { error: actionResponse.serverError });
          clearCartCache();
          return;
        }

        if (actionResponse.validationErrors) {
          log.error('validationErrors', {
            error: actionResponse.validationErrors,
          });
          clearCartCache();
          return;
        }

        if (!actionResponse.data) {
          log.error('actionResponse.data is undefined after error checks');
          clearCartCache();
          return;
        }
        const resData = actionResponse.data;

        if (resData.error || !resData.success || !resData.lineItem) {
          log.error('error from action data', { error: resData?.error });
          clearCartCache();
          return;
        }

        if (resData.type === 'delete') {
          const lineItemNode = resData.lineItem.node;

          trackRemoveFromCart({
            list: previousPage?.collection ?? '',
            products: [
              serializeCartItemToElevarProduct({
                item: lineItemNode,
                domain,
                list: previousPage?.collection ?? '',
                quantity: item.quantity,
              }),
            ],
          });
        } else if (resData.type === 'update' && type === 'plus') {
          const lineItemData = resData.lineItem;

          trackAddToCart({
            list: previousPage?.collection ?? '',
            products: [
              serializeCartItemToElevarProduct({
                item: lineItemData,
                domain,
                list: previousPage?.collection ?? '',
                quantity: 1,
              }),
            ],
          });
        } else if (resData.type === 'update' && type === 'minus') {
          const lineItemData = resData.lineItem;

          trackRemoveFromCart({
            list: previousPage?.collection ?? '',
            products: [
              serializeCartItemToElevarProduct({
                item: lineItemData,
                domain,
                list: previousPage?.collection ?? '',
                quantity: 1,
              }),
            ],
          });
        }
        clearCartCache();
      } finally {
        setLoading(false);
      }
    });
  };

  return { editQuantity, loading };
}
